import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useGridFilter } from "../lib/useGridFilter";
import { FilterPopover } from "../FilterPopover";
import { SheetFrame, StatStrip } from "../SheetFrame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RefreshCw, FilterX } from "lucide-react";

const columns = [
  { key: "group", label: "Group Name (Quality)", width: 160 },
  { key: "company", label: "Company", width: 160 },
  { key: "item", label: "Item Name", width: 230 },
  { key: "shade", label: "Shade", width: 100 },
  { key: "ordered_qty", label: "Ordered (Than)", width: 125, numeric: true },
  { key: "received_qty", label: "Received / In Stock (Than)", width: 175, numeric: true },
  { key: "balance_qty", label: "Balance (Than)", width: 125, numeric: true },
  { key: "remark", label: "Remark", width: 220 },
];

const TAG_BG = {
  "COMPANY BALANCE ORDER": "#EEF2FF",
  "WHOLESALE BALANCE ORDER": "#ECFEFF",
};

export default function BalanceOrder() {
  const [data, setData] = useState(null);
  const [pendingOnly, setPendingOnly] = useState(true);
  const [tag, setTag] = useState("ALL");
  const rows = (data?.rows || [])
    .filter((r) => (pendingOnly ? r.balance_qty > 0 : true))
    .filter((r) => (tag === "ALL" ? true : r.remark === tag));
  const grid = useGridFilter(rows, columns);

  const load = useCallback(
    () => api.balanceOrder().then(setData).catch((e) => toast.error(String(e))),
    []
  );
  useEffect(() => { load(); }, [load]);

  const totals = grid.visible.reduce(
    (a, r) => ({
      ordered: a.ordered + r.ordered_qty,
      received: a.received + r.received_qty,
      balance: a.balance + r.balance_qty,
    }),
    { ordered: 0, received: 0, balance: 0 }
  );

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <SheetFrame
      testId="bo-page"
      title="Balance Order"
      subtitle="Read only · conference orders (ordered − arrived) and wholesale orders (ordered − in house stock) in one sheet"
      actions={
        <>
          {[
            ["ALL", "ALL"],
            ["COMPANY BALANCE ORDER", "CONFERENCE"],
            ["WHOLESALE BALANCE ORDER", "WHOLESALE"],
          ].map(([value, label]) => (
            <Button
              key={value}
              variant={tag === value ? "default" : "outline"}
              size="sm"
              className="h-8 text-[10px]"
              data-testid={`bo-tag-${label.toLowerCase()}-btn`}
              onClick={() => setTag(value)}
            >
              {label}
            </Button>
          ))}
          <Input
            data-testid="bo-global-search"
            value={grid.search}
            onChange={(e) => grid.setSearch(e.target.value)}
            placeholder="SEARCH ANY COLUMN"
            className="h-8 w-44 text-xs"
          />
          {grid.activeCount > 0 && (
            <Button variant="outline" size="sm" className="h-8 text-[10px]" data-testid="bo-clear-filters-btn" onClick={grid.clearAll}>
              <FilterX className="h-3.5 w-3.5 mr-1" /> CLEAR ({grid.activeCount})
            </Button>
          )}
          <Button variant={pendingOnly ? "default" : "outline"} size="sm" className="h-8 text-[10px]"
            data-testid="bo-show-all-btn" onClick={() => setPendingOnly((p) => !p)}>
            {pendingOnly ? "PENDING ONLY" : "SHOWING ALL"}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-[10px]" data-testid="bo-refresh-btn" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> REFRESH
          </Button>
        </>
      }
      stats={
        <StatStrip
          items={[
            ["Lines pending", data.pending_lines, "bo-stat-pending"],
            ["Total ordered", data.total_ordered, "bo-stat-ordered"],
            ["Received / in stock", data.total_received, "bo-stat-received"],
            ["Balance", data.total_balance, "bo-stat-balance"],
          ]}
        />
      }
    >
      <div className="sheet-scroll" data-testid="bo-grid">
        <table className="border-collapse w-max min-w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#0A2540] text-white">
              <th className="w-12 border-r border-[#123a5c] px-2 py-2 text-xs font-semibold">SR</th>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`border-r border-[#123a5c] px-2 py-2 text-xs font-bold uppercase tracking-wide ${c.numeric ? "text-right" : "text-left"}`}
                  style={{ width: c.width, minWidth: c.width }}
                >
                  {c.label}
                  {grid.sort?.key === c.key && (grid.sort.dir === "asc" ? " ↑" : " ↓")}
                </th>
              ))}
            </tr>
            <tr className="bg-[color:var(--sheet-head2)]">
              <th />
              {columns.map((c) => (
                <th key={c.key} className="border-r border-b border-[color:var(--sheet-border)] p-1">
                  <FilterPopover
                    column={c}
                    filter={grid.filters[c.key]}
                    onChange={(f) => grid.setFilter(c.key, f)}
                    rows={rows}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    prefix="bo-"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.visible.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="p-4 text-sm text-muted-foreground">
                  Nothing pending — every company order is covered.
                </td>
              </tr>
            )}
            {grid.visible.map((r, i) => (
              <tr
                key={`${r.remark}-${r.item}-${r.shade}`}
                className="row-hover"
                style={{ background: TAG_BG[r.remark] || "var(--sheet-bg)" }}
                data-testid={`bo-row-${i}`}
              >
                <td className="border-r border-b border-[color:var(--sheet-border)] text-center text-[10px] text-muted-foreground h-8">
                  {i + 1}
                </td>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-testid={`bo-cell-${i}-${c.key}`}
                    className={`border-r border-b border-[color:var(--sheet-border)] px-2 text-sm mono ${c.numeric ? "text-right" : ""} ${
                      c.key === "balance_qty" && r.balance_qty > 0 ? "font-bold text-[#0066FF]" : ""
                    } ${c.key === "remark" ? "text-[11px] font-semibold tracking-wide" : ""}`}
                  >
                    {String(r[c.key] ?? "").trim() === "" ? "—" : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0">
            <tr className="bg-[#3F6F52] text-white">
              <td className="px-2 py-2 text-xs">Σ</td>
              <td colSpan={4} className="px-2 py-2 text-xs">{grid.visible.length} lines shown</td>
              <td className="px-2 py-2 text-xs text-right mono" data-testid="bo-total-ordered">{totals.ordered}</td>
              <td className="px-2 py-2 text-xs text-right mono" data-testid="bo-total-received">{totals.received}</td>
              <td className="px-2 py-2 text-xs text-right mono" data-testid="bo-total-balance">{totals.balance}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </SheetFrame>
  );
}
